/**
 * Read-only information tabs for the RFQ detail page: Overview, Line Items,
 * Certificates and Documents. Presentational — formatting comes from the
 * page via props so every surface uses the tenant's formatters.
 */
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { TabsContent } from '../ui/tabs';
import { FileText, Shield } from 'lucide-react';
import type { RFQ } from '../../lib/api';
import { toNumber, toNullableNumber } from './rfqFormat';
import RFQDocumentUpload from './RFQDocumentUpload';

interface RFQDetailInfoTabsProps {
  rfq: RFQ;
  formatMoney: (value: unknown) => string;
  formatDate: (date: string) => string;
  /** Suppliers read the buyer's documents; only the buyer attaches them. */
  canAttachDocuments?: boolean;
  onDocumentUploaded?: () => void;
}

export default function RFQDetailInfoTabs({
  rfq, formatMoney, formatDate, canAttachDocuments = false, onDocumentUploaded,
}: RFQDetailInfoTabsProps) {
  return (
    <>
      <TabsContent value="overview" className="space-y-4">
        <Card>
          <CardHeader><CardTitle>Description</CardTitle></CardHeader>
          <CardContent><p className="whitespace-pre-wrap text-sm">{rfq.description}</p></CardContent>
        </Card>
        {rfq.special_requirements && (
          <Card>
            <CardHeader><CardTitle>Special Requirements</CardTitle></CardHeader>
            <CardContent><p className="whitespace-pre-wrap text-sm">{rfq.special_requirements}</p></CardContent>
          </Card>
        )}
        {rfq.terms_and_conditions && (
          <Card>
            <CardHeader><CardTitle>Terms &amp; Conditions</CardTitle></CardHeader>
            <CardContent><p className="whitespace-pre-wrap text-sm">{rfq.terms_and_conditions}</p></CardContent>
          </Card>
        )}
        <Card>
          <CardHeader><CardTitle>Additional Information</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Issue Date:</span>
              <span className="font-medium">{rfq.issue_date ? formatDate(rfq.issue_date) : 'Not issued'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Validity Period:</span>
              <span className="font-medium">{rfq.validity_period_days} days</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Currency:</span>
              <span className="font-medium">{rfq.currency}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Created:</span>
              <span className="font-medium">{formatDate(rfq.created_at)}</span>
            </div>
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="line-items" className="space-y-4">
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium">#</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">Description</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">Part Number</th>
                    <th className="px-4 py-3 text-right text-sm font-medium">Quantity</th>
                    <th className="px-4 py-3 text-right text-sm font-medium">Unit Price</th>
                    <th className="px-4 py-3 text-right text-sm font-medium">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {rfq.line_items?.map((item) => {
                    const target = toNullableNumber(item.target_unit_price);
                    return (
                      <tr key={item.id} className="hover:bg-muted/50">
                        <td className="px-4 py-3 text-sm">{item.line_number}</td>
                        <td className="px-4 py-3">
                          <div className="font-medium">{item.item_description}</div>
                          {item.technical_specifications && (
                            <div className="text-xs text-muted-foreground mt-1">{item.technical_specifications}</div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm">{item.part_number}</td>
                        <td className="px-4 py-3 text-sm text-right">
                          {toNumber(item.quantity).toLocaleString()} {item.unit_of_measure}
                        </td>
                        <td className="px-4 py-3 text-sm text-right">{target !== null ? formatMoney(target) : '-'}</td>
                        <td className="px-4 py-3 text-sm text-right font-medium">{formatMoney(item.estimated_total_price)}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="bg-muted font-medium">
                  <tr>
                    <td colSpan={5} className="px-4 py-3 text-right">Total Estimated Value:</td>
                    <td className="px-4 py-3 text-right">{formatMoney(rfq.total_estimated_value)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="certificates" className="space-y-4">
        {rfq.required_certificates?.length > 0 ? (
          <div className="grid gap-4">
            {rfq.required_certificates.map((cert) => (
              <Card key={cert.id}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <Shield className="h-5 w-5 text-primary" />
                      <CardTitle className="text-lg">{cert.certificate_name}</CardTitle>
                    </div>
                    <Badge variant={cert.is_mandatory ? 'default' : 'secondary'}>
                      {cert.is_mandatory ? 'MANDATORY' : 'OPTIONAL'}
                    </Badge>
                  </div>
                  <CardDescription className="capitalize">{cert.certificate_type.replace('_', ' ')}</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-sm mb-2">{cert.description}</p>
                  <p className="text-xs text-muted-foreground">
                    Validity required: {cert.expiry_tolerance_days} days beyond delivery
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="text-center py-12 text-muted-foreground">No certificates required for this RFQ</CardContent>
          </Card>
        )}
      </TabsContent>

      <TabsContent value="documents" className="space-y-4">
        {canAttachDocuments && (
          <RFQDocumentUpload rfqId={rfq.id} onUploaded={() => onDocumentUploaded?.()} />
        )}
        {rfq.documents?.length > 0 ? (
          <div className="grid gap-4">
            {rfq.documents.map((doc) => (
              <Card key={doc.id}>
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    <CardTitle className="text-lg">{doc.title}</CardTitle>
                  </div>
                  <CardDescription className="capitalize">{doc.document_type.replace('_', ' ')}</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-sm mb-2">{doc.description}</p>
                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    <span>File: {doc.file_name}</span>
                    <span>Uploaded: {formatDate(doc.uploaded_at)}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="text-center py-12 text-muted-foreground">
              {canAttachDocuments
                ? 'No documents attached yet — attach drawings or specifications above.'
                : 'No documents attached to this RFQ'}
            </CardContent>
          </Card>
        )}
      </TabsContent>
    </>
  );
}
